import { useState } from 'react';
import { can, formatJalali, formatPercent, formatRial, parseTranches } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMarginFloors } from '../config/ConfigProvider';
import { marginColor } from '../lib/margin';
import {
  Button,
  Card,
  Chip,
  Field,
  Input,
  Modal,
  useToast,
  NumberInput,
  JalaliDateInput,
  Textarea,
} from '../ui';

interface DeliveryRow {
  id: number;
  product_code: string;
  date: string;
  qty: number;
  notes: string;
  /** unit COGS frozen at dispatch — the authoritative exit-cost anchor */ cost_locked: number;
  /** set when this delivery was reversed (E16-18); '' = live */ reversed_at: string;
  reversed_by: string;
}
interface ChequeRow {
  id: number;
  direction: 'in' | 'out';
  kind: string;
  amount: number;
  due_date: string;
  status: string;
  notes: string;
}
interface LineInfo {
  product_code: string;
  name: string;
  /** net effective unit price after the per-line discount */ price: number;
  /** gross unit price before the discount (WS-C) */ grossUnitPrice: number;
  /** absolute rial discount off the line total */ discount: number;
  qty: number;
  unitFull: number;
  /** COGS build-up behind unitFull — shown at the release verdict so the owner sees WHY the margin is what it is (E16-04). */
  material: number;
  freight: number;
  conversion: number;
  /** margin at the day price — the figure the release gate measures */
  dayMargin: number | null;
  /** per-unit COGS frozen at Offer/Confirm (the snapshot anchor); null for a post-Offer line */
  snapUnitFull: number | null;
  /** 0–1 cost-confidence ratio (share of COGS from actual, not group-estimated, prices) — UX-5 provenance */
  confirmed: number;
  /** un-priced material group|unit keys — non-empty ⇒ COGS incomplete (hard-blocks release) — UX-5 provenance */
  missing: string[];
  delivered: number;
  remaining: number;
  /** Release-gate verdict PREVIEW (M1) — the same gate the deliveries route enforces. */
  release?: {
    verdict: 'ok' | 'override' | 'blocked';
    blocking: { code: string; message: string }[];
    warnings: { code: string; message: string }[];
  };
}
interface TrancheStatusRow {
  index: number;
  label: string;
  date: string;
  amount: number;
  covered: number;
  remaining: number;
  state: 'settled' | 'overdue' | 'due_soon' | 'upcoming';
  daysLate: number;
}
export interface DealExtrasData {
  id: number;
  state: string;
  /** why the deal's line items can't be edited (E16-20); empty reasons ⇒ editable */
  itemsLock?: { locked: boolean; reasons: ('deliveries' | 'payments' | 'release')[] };
  /** dollar view (informational only) — the offer + remainder in USD; null when no FX data */
  dollarView?: {
    usdAtOffer: number;
    usdNow: number;
    usdChangePct: number;
    offerUsd: number;
    remainderUsdAtOffer: number;
    remainderUsdNow: number;
    remainderRialDayPrice: number;
    dollarHoldRial: number;
    gapRial: number;
    gapUsd: number;
  } | null;
  deliveries: DeliveryRow[];
  chequeList: ChequeRow[];
  /** per-tranche settlement status («کدام قسط عقب است») — the cash calendar's own allocation */
  tranches?: TrancheStatusRow[];
  /** payment-terms inflation risk (M8): per-tranche rials-at-risk from projected material drift until the
   *  money lands. Server already computes it; the tranche table reads it so the owner sees WHICH installment
   *  leaks HOW MUCH (Move 2) — not just the deal-level worst-tranche headline. */
  termsRisk?: {
    atRisk: boolean;
    totalRiskRial: number;
    risks: { label: string; date: string; projectedDrift: number; riskRial: number }[];
  };
  /** margin realization (Move 4): the frozen offer estimate vs what the shipped units actually made, with the
   *  gap split into cost-drift vs price/mix. Present once anything has shipped (`delivered`). */
  realization?: {
    delivered: boolean;
    deliveredRevenue: number;
    deliveredCost: number;
    realizedMargin: number | null;
    offerMargin: number | null;
    gapPoints: number | null;
    costDriftPoints: number | null;
    priceMixPoints: number | null;
    costDriftRial: number;
  };
  receivable: { collected: number; balance: number; pendingCarry: number };
  /** the deal's payment schedule (JSON) — its tranches let a cheque be tagged to the one it settles */
  payment_schedule?: string | null;
  compute: {
    revenue: number;
    lines: LineInfo[];
    /** worst per-line cost-confidence across the deal (UX-5 header grain) */
    worstConfirmed: number;
    /** any deal line has an un-priced group (UX-5 header grain) */
    anyMissing: boolean;
    // Proportional price-fix (MATH_SPEC §15) — derived from actual cleared payments.
    dayValue: number;
    lockedValue: number;
    lockedFraction: number;
    blendedRevenue: number;
    blendedGross: number;
    blendedMargin: number | null;
    floatingReceivable: number;
  };
}

const CHQ_STATUS_FA: Record<string, string> = {
  pending: 'در جریان',
  cleared: 'وصول‌شده',
  bounced: 'برگشتی',
  replaced: 'جایگزین',
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * «سیر بها» — the per-line COGS TIMELINE (the owner's key: the cost story per order at every lifecycle
 * point): offer (snapshot anchor) → today (live) → dispatch (qty-weighted mean of the frozen
 * `cost_locked`). Pure display over anchors already in the payload — nothing recomputes or moves money.
 */
function CostTimelineCard({ deal }: { deal: DealExtrasData }) {
  const rows = deal.compute.lines
    .map((l) => {
      const dels = deal.deliveries.filter((d) => d.product_code === l.product_code);
      const delQty = dels.reduce((s, d) => s + d.qty, 0);
      const dispatched =
        delQty > 0 ? dels.reduce((s, d) => s + d.qty * (d.cost_locked || 0), 0) / delQty : null;
      const drift =
        l.snapUnitFull != null && l.snapUnitFull > 0
          ? (l.unitFull - l.snapUnitFull) / l.snapUnitFull
          : null;
      return { ...l, dispatched, drift };
    })
    .filter((r) => r.snapUnitFull != null || r.dispatched != null);
  if (!rows.length) return null;

  return (
    <Card title="سیر بها — پیشنهاد ← امروز ← ترخیص">
      <table className="table">
        <thead>
          <tr>
            <th>محصول</th>
            <th>بها در پیشنهاد</th>
            <th>بهای امروز</th>
            <th>بهای ترخیص (میانگین)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.product_code}>
              <td>
                {r.name}
                <div className="muted">{r.product_code}</div>
              </td>
              <td>{r.snapUnitFull != null ? formatRial(Math.round(r.snapUnitFull)) : '—'}</td>
              <td>
                {formatRial(Math.round(r.unitFull))}
                {r.drift != null && Math.abs(r.drift) >= 0.005 ? (
                  <span style={{ color: r.drift > 0 ? 'var(--red)' : 'var(--green)' }}>
                    {' '}
                    ({r.drift > 0 ? '+' : ''}
                    {formatPercent(r.drift)})
                  </span>
                ) : null}
              </td>
              <td>{r.dispatched != null ? formatRial(Math.round(r.dispatched)) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted" style={{ marginBottom: 0 }}>
        بهای «پیشنهاد» و «ترخیص» لنگرهای منجمد و رسمی‌اند؛ «امروز» زنده است. برای هر تاریخ دلخواه:
        BOM ← «بها در تاریخ».
      </p>
    </Card>
  );
}

/**
 * Margin realization (Move 4) — the loop-closer: «I offered X%; what did the SHIPPED units actually make,
 * and why the gap?» The gap is split by an exact bridge into cost-drift (material rose offer→dispatch) and
 * price/mix. Pure display over the frozen anchors already in the payload; only shown once something ships.
 */
function MarginRealizationCard({ deal }: { deal: DealExtrasData }) {
  const floors = useMarginFloors();
  const r = deal.realization;
  if (!r?.delivered) return null;
  // A margin-point difference, shown as whole «واحد» (percentage points). Sign convention per slice:
  // POSITIVE = it HURT the margin (shortfall), NEGATIVE = it helped. Colour carries the good/bad read.
  const pts = (v: number | null) => {
    if (v == null) return '—';
    const n = Math.round(v * 100); // round FIRST so a rounding-to-zero gap never shows a spurious «−0»
    return `${n < 0 ? '−' : ''}${Math.abs(n)} واحد`;
  };
  const shortfall = (r.gapPoints ?? 0) > 0;
  return (
    <Card title="تحقق حاشیه — پیشنهاد ← محقق‌شده">
      <div style={{ lineHeight: 2 }}>
        حاشیهٔ پیشنهاد (منجمد):{' '}
        <b style={{ color: marginColor(r.offerMargin, floors) }}>{formatPercent(r.offerMargin)}</b>
        {' ← '}حاشیهٔ محقق‌شده (واحدهای تحویل‌شده):{' '}
        <b style={{ color: marginColor(r.realizedMargin, floors) }}>
          {formatPercent(r.realizedMargin)}
        </b>
        {r.gapPoints != null ? (
          <>
            {' · '}شکاف{' '}
            <b style={{ color: shortfall ? 'var(--red)' : 'var(--green)' }}>{pts(r.gapPoints)}</b>
          </>
        ) : null}
      </div>
      {r.costDriftPoints != null && r.priceMixPoints != null ? (
        <div className="muted" style={{ fontSize: 12 }}>
          تفکیک شکاف: دریفت بها {pts(r.costDriftPoints)} · قیمت/ترکیب {pts(r.priceMixPoints)}
          {r.costDriftRial !== 0
            ? ` — دریفت بها روی واحدهای تحویل‌شده: ${formatRial(r.costDriftRial)}`
            : ''}
        </div>
      ) : null}
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        «محقق‌شده» از بها/قیمتِ منجمدِ لحظهٔ ترخیص، فقط روی واحدهای تحویل‌شده. «دریفت بها» = رشد
        بهای مواد از پیشنهاد تا ترخیص؛ «قیمت/ترکیب» = بقیهٔ شکاف (قیمت و ترکیب اقلامِ تحویل‌شده).
      </p>
    </Card>
  );
}

/** Warehouse + cheque panels shown on Confirmed/Delivered/Closed deals. */
export function DealExtras({ deal, onChanged }: { deal: DealExtrasData; onChanged: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const remaining = deal.compute.lines.filter((l) => l.remaining > 1e-9);

  // delivery form
  const [dlCode, setDlCode] = useState(remaining[0]?.product_code ?? '');
  // Release-gate preview for the currently-selected line (M1): the operator sees the verdict up front.
  const selLine = remaining.find((r) => r.product_code === dlCode);
  const selectedRelease = selLine?.release;
  const [dlQty, setDlQty] = useState('');
  const [dlDate, setDlDate] = useState(today());
  const [dlNotes, setDlNotes] = useState('');
  // reverse-entry correction (E16-18): the row to reverse (opens the reason modal)
  const [reverseFor, setReverseFor] = useState<DeliveryRow | null>(null);
  const canReverse = user ? can(user.role, 'delivery.write') : false;
  // release-gate block state (422 release_blocked): show the reasons; Admin may override with a reason.
  const [blocked, setBlocked] = useState<{ message: string; overridable: boolean } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  // cheque form
  const [chDir, setChDir] = useState<'in' | 'out'>('in');
  const [chAmt, setChAmt] = useState('');
  const [chDue, setChDue] = useState('');
  const [chStatus, setChStatus] = useState('pending');
  const [chKind, setChKind] = useState('cheque');
  const [chNotes, setChNotes] = useState('');
  const [chTranche, setChTranche] = useState('');
  const tranches = parseTranches(deal.payment_schedule ?? null);

  if (!['Confirmed', 'Delivered', 'Closed'].includes(deal.state)) return null;

  async function saveDelivery(withOverride = false) {
    const qty = Number(dlQty);
    if (!(qty > 0)) return toast('تعداد تحویل باید بزرگ‌تر از صفر باشد', 'error');
    if (withOverride && overrideReason.trim().length < 10)
      return toast('دلیل ترخیص باید حداقل ۱۰ حرف باشد', 'error');
    const r = await api('/api/deliveries', {
      method: 'POST',
      body: JSON.stringify({
        deal_id: deal.id,
        product_code: dlCode,
        qty,
        date: dlDate,
        notes: dlNotes,
        ...(withOverride ? { override_reason: overrideReason.trim() } : {}),
      }),
    });
    if (!r.ok) {
      const body = r.body as {
        error?: string;
        message?: string;
        details?: { overridable?: boolean };
      };
      // The release gate refused: surface the reasons; an Admin gets the conscious-override box.
      if (body.error === 'release_blocked') {
        setBlocked({
          message: body.message ?? '',
          overridable: body.details?.overridable === true,
        });
        return;
      }
      return toast(body.message ?? 'خطا در ثبت تحویل', 'error');
    }
    toast(withOverride ? 'با تأیید مدیر ترخیص شد' : 'تحویل ثبت شد');
    setDlQty('');
    setDlNotes('');
    setBlocked(null);
    setOverrideReason('');
    onChanged();
  }

  async function saveCheque() {
    const amount = Number(chAmt);
    if (!(amount > 0)) return toast('مبلغ باید بزرگ‌تر از صفر باشد', 'error');
    // A PENDING چک / L/C without a due date drops out of the cash-calendar forecast (which buckets by due
    // date), so require it for deferred pending instruments up front — the server re-validates (E7-05).
    if ((chKind === 'cheque' || chKind === 'LC') && chStatus === 'pending' && !chDue) {
      return toast('تاریخ سررسید برای چک/ال‌سی لازم است', 'error');
    }
    const r = await api('/api/cheques', {
      method: 'POST',
      body: JSON.stringify({
        deal_id: deal.id,
        direction: chDir,
        amount,
        due_date: chDue,
        status: chStatus,
        kind: chKind,
        notes: chNotes,
        tranche: chDir === 'in' && chTranche !== '' ? Number(chTranche) : undefined,
      }),
    });
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('ثبت شد');
    setChAmt('');
    setChNotes('');
    setChTranche('');
    onChanged();
  }

  return (
    <>
      <Card title="انبار و تحویل">
        <div className="totalbox">
          وضعیت تحویل:{' '}
          {remaining.length ? (
            <>باقی‌مانده: {remaining.map((r) => `${r.name} (${r.remaining})`).join('، ')}</>
          ) : (
            <b>کامل تحویل شد</b>
          )}
        </div>
        {remaining.length ? (
          <>
            <div className="form-grid" style={{ marginTop: 10 }}>
              <Field label="محصول">
                <select
                  className="input"
                  value={dlCode}
                  onChange={(e) => setDlCode(e.target.value)}
                >
                  {remaining.map((r) => (
                    <option key={r.product_code} value={r.product_code}>
                      {r.name} (باقی {r.remaining})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="تعداد تحویل">
                <NumberInput
                  allowDecimal
                  value={dlQty}
                  onValue={(n) => setDlQty(n ? String(n) : '')}
                />
              </Field>
              <Field label="تاریخ">
                <JalaliDateInput value={dlDate} onValue={setDlDate} />
              </Field>
              <Field label="یادداشت / راننده">
                <Input value={dlNotes} onChange={(e) => setDlNotes(e.target.value)} />
              </Field>
            </div>
            {/* Whole-deal readiness — every remaining line's verdict, so an ✅ on the selected line never hides
                a ⛔/⚠ on another line of the same deal. Only shown when there is more than one line to ship. */}
            {remaining.length > 1 ? (
              <div style={{ fontSize: 12, color: 'var(--ink-2)', margin: '8px 0 0' }}>
                وضعیت ترخیصِ اقلام این قرارداد:{' '}
                {remaining.map((r, i) => (
                  <span key={r.product_code}>
                    {i > 0 ? ' · ' : ''}
                    {r.release?.verdict === 'ok'
                      ? '✅'
                      : r.release?.verdict === 'blocked'
                        ? '⛔'
                        : r.release?.verdict === 'override'
                          ? '⚠'
                          : '·'}{' '}
                    {r.product_code}
                  </span>
                ))}
              </div>
            ) : null}
            {selectedRelease ? (
              <div
                style={{
                  margin: '8px 0',
                  fontSize: 13,
                  color:
                    selectedRelease.verdict === 'ok'
                      ? 'var(--green)'
                      : selectedRelease.verdict === 'blocked'
                        ? 'var(--red)'
                        : 'var(--amber, #b45309)',
                }}
              >
                {selectedRelease.verdict === 'ok' ? (
                  <b>✅ این قلم آزاد برای ترخیص</b>
                ) : (
                  <>
                    <b>
                      {selectedRelease.verdict === 'blocked'
                        ? '⛔ این قلم غیرقابل ترخیص'
                        : '⚠ این قلم نیازمند تأیید مدیر'}
                    </b>{' '}
                    — {selectedRelease.blocking.map((c) => c.message).join(' • ')}
                  </>
                )}
                {/* WHY this verdict (E16-04): the cost build-up + margin behind the gate, so the owner acts on the
                    driver — مواد/حمل/دستمزد+سربار — not just the pass/fail. */}
                {selLine ? (
                  <div style={{ marginTop: 4, color: 'var(--ink-2)', fontSize: 12 }}>
                    بها {formatRial(selLine.unitFull)}{' '}
                    <span className="muted">
                      (مواد {formatRial(selLine.material)} · حمل {formatRial(selLine.freight)} ·
                      دستمزد و سربار {formatRial(selLine.conversion)})
                    </span>{' '}
                    · قیمت فروش {formatRial(selLine.price)} · حاشیهٔ روز{' '}
                    <b>{selLine.dayMargin == null ? '—' : formatPercent(selLine.dayMargin)}</b>
                  </div>
                ) : null}
              </div>
            ) : null}
            <Button variant="secondary" onClick={() => void saveDelivery()}>
              ثبت تحویل
            </Button>
            {blocked ? (
              <div
                className="banner"
                style={{
                  marginTop: 10,
                  borderRight: '4px solid var(--red)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <b style={{ flex: 1, minWidth: 220 }}>⛔ {blocked.message}</b>
                {blocked.overridable && user?.role === 'Admin' ? (
                  <>
                    <div style={{ flexBasis: '100%' }} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <Input
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="دلیل ترخیص با تأیید مدیر (حداقل ۱۰ حرف — در رویدادها ثبت می‌شود)"
                      />
                    </div>
                    <Button onClick={() => void saveDelivery(true)}>ترخیص با تأیید مدیر</Button>
                  </>
                ) : blocked.overridable ? (
                  <span className="muted">ترخیص تنها با تأیید مدیر ممکن است.</span>
                ) : (
                  <span className="muted">
                    این مورد قابل‌ترخیص نیست — ابتدا قیمت مواد را تکمیل کنید.
                  </span>
                )}
              </div>
            ) : null}
          </>
        ) : null}
        {deal.deliveries.length ? (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>تاریخ</th>
                <th>محصول</th>
                <th>تعداد</th>
                <th>یادداشت</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deal.deliveries.map((d) => {
                const reversed = !!d.reversed_at;
                return (
                  <tr key={d.id} style={reversed ? { opacity: 0.5 } : undefined}>
                    <td>{formatJalali(d.date)}</td>
                    <td style={reversed ? { textDecoration: 'line-through' } : undefined}>
                      {d.product_code}
                    </td>
                    <td style={reversed ? { textDecoration: 'line-through' } : undefined}>
                      {d.qty}
                    </td>
                    <td>{d.notes || '—'}</td>
                    <td>
                      {reversed ? (
                        <Chip tone="neutral">برگشت‌خورده</Chip>
                      ) : canReverse ? (
                        <Button variant="ghost" onClick={() => setReverseFor(d)}>
                          برگشت
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </Card>

      {reverseFor ? (
        <ReverseDeliveryModal
          delivery={reverseFor}
          onClose={() => setReverseFor(null)}
          onDone={() => {
            setReverseFor(null);
            onChanged();
          }}
        />
      ) : null}

      <MarginRealizationCard deal={deal} />
      <CostTimelineCard deal={deal} />

      <Card title="چک‌ها و دریافتنی">
        {/* Proportional price-fix (MATH_SPEC §15): the price is locked to the share actually prepaid;
            the rest floats to today's day price. All derived from cleared payments — no manual toggle. */}
        <div className="totalbox">
          <b>درآمد ترکیبی (به‌روز): {formatRial(deal.compute.blendedRevenue)}</b>
          {'  '}
          <span className="muted">
            (پیش‌فاکتور: {formatRial(deal.compute.revenue)} · ارزش روز:{' '}
            {formatRial(deal.compute.dayValue)})
          </span>
        </div>
        {/* The HONEST blended gross/margin: lock-blended revenue (frozen-low on the prepaid share) minus
            today's ACTUAL cost — the same margin the close/realization card will book (it erodes if a
            prepaid deal's cost rose), not an optimistic "held" figure the ledger never confirms. */}
        <div className="totalbox" style={{ marginTop: 6 }}>
          سود ترکیبی: <b>{formatRial(deal.compute.blendedGross)}</b> · حاشیهٔ ترکیبی:{' '}
          <b>
            {deal.compute.blendedMargin != null ? formatPercent(deal.compute.blendedMargin) : '—'}
          </b>
        </div>
        <div className="totalbox" style={{ marginTop: 6 }}>
          قیمت تثبیت‌شده: <b>{formatPercent(deal.compute.lockedFraction)}</b> —{' '}
          {formatRial(deal.compute.lockedValue)} · سهم شناور (به نرخ روز):{' '}
          <b>{formatPercent(1 - deal.compute.lockedFraction)}</b> —{' '}
          {formatRial(deal.compute.floatingReceivable)}
        </div>
        <div className="totalbox" style={{ marginTop: 6 }}>
          وصول‌شده: {formatRial(deal.receivable.collected)} ·{' '}
          <b>مانده: {formatRial(deal.receivable.balance)}</b> · هزینهٔ حمل تورمی:{' '}
          {formatRial(deal.receivable.pendingCarry)}
        </div>
        {deal.tranches?.length
          ? (() => {
              // Payment-terms inflation risk per tranche (Move 2): show WHICH installment leaks HOW MUCH to
              // projected material drift before the money lands — the deal-level warning names only the worst
              // one. Keyed by label (the codebase's tranche identity, as termsInflationWarning uses). The column
              // appears only when the deal actually carries risk, so quiet deals stay uncluttered.
              const riskByLabel = new Map(
                (deal.termsRisk?.risks ?? []).map((r) => [r.label, r] as const),
              );
              const showRisk = !!deal.termsRisk?.atRisk;
              return (
                <table className="table" style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>قسط</th>
                      <th>سررسید</th>
                      <th>مبلغ</th>
                      <th>پوشش</th>
                      <th>مانده</th>
                      <th>وضعیت</th>
                      {showRisk ? (
                        <th title="ارزش در معرض تورم مواد تا زمان وصول">در معرض تورم</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {deal.tranches.map((t) => {
                      const risk = riskByLabel.get(t.label);
                      return (
                        <tr key={t.index}>
                          <td>{t.label || `قسط ${t.index + 1}`}</td>
                          <td>{formatJalali(t.date)}</td>
                          <td>{formatRial(t.amount)}</td>
                          <td>{formatRial(t.covered)}</td>
                          <td>{t.remaining > 0 ? formatRial(t.remaining) : '—'}</td>
                          <td>
                            {t.state === 'settled' ? (
                              <span style={{ color: 'var(--green)' }}>تسویه‌شده ✓</span>
                            ) : t.state === 'overdue' ? (
                              <b style={{ color: 'var(--red)' }}>عقب‌افتاده ({t.daysLate} روز)</b>
                            ) : t.state === 'due_soon' ? (
                              <span style={{ color: 'var(--amber, #b58900)' }}>نزدیک سررسید</span>
                            ) : (
                              <span className="muted">آتی</span>
                            )}
                          </td>
                          {showRisk ? (
                            <td>
                              {risk && risk.riskRial > 0 ? (
                                <span
                                  style={{ color: 'var(--amber, #b58900)' }}
                                  title={`~${formatPercent(risk.projectedDrift)} دریفت مواد تا وصول`}
                                >
                                  {formatRial(risk.riskRial)} ⚠
                                </span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()
          : null}
        <div className="form-grid" style={{ marginTop: 10 }}>
          <Field label="جهت">
            <select
              className="input"
              value={chDir}
              onChange={(e) => setChDir(e.target.value as 'in' | 'out')}
            >
              <option value="in">دریافتی (از مشتری)</option>
              <option value="out">پرداختی (به تأمین‌کننده)</option>
            </select>
          </Field>
          {chDir === 'in' && tranches.length > 1 ? (
            <Field label="قسط مرتبط (برای زمان‌بندی دقیق دریافتنی)">
              <select
                className="input"
                value={chTranche}
                onChange={(e) => setChTranche(e.target.value)}
              >
                <option value="">— نامشخص (زودترین قسط) —</option>
                {tranches.map((t, i) => (
                  <option key={i} value={i}>
                    {t.label || `قسط ${i + 1}`} ({t.pct}٪)
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field label="مبلغ (﷼)">
            <NumberInput value={chAmt} onValue={(n) => setChAmt(n ? String(n) : '')} />
          </Field>
          <Field label="تاریخ سررسید">
            <JalaliDateInput value={chDue} onValue={setChDue} />
          </Field>
          <Field label="وضعیت">
            <select
              className="input"
              value={chStatus}
              onChange={(e) => setChStatus(e.target.value)}
            >
              <option value="pending">در جریان</option>
              <option value="cleared">وصول‌شده</option>
              <option value="bounced">برگشتی</option>
            </select>
          </Field>
          <Field label="نوع">
            <select className="input" value={chKind} onChange={(e) => setChKind(e.target.value)}>
              <option value="cheque">چک</option>
              <option value="LC">L/C</option>
              <option value="cash">نقدی/حواله</option>
            </select>
          </Field>
          <Field label="یادداشت">
            <Input value={chNotes} onChange={(e) => setChNotes(e.target.value)} />
          </Field>
        </div>
        <Button variant="secondary" onClick={saveCheque}>
          ثبت چک / پرداخت
        </Button>
        {deal.chequeList.length ? (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>جهت</th>
                <th>مبلغ</th>
                <th>سررسید</th>
                <th>وضعیت</th>
                <th>یادداشت</th>
              </tr>
            </thead>
            <tbody>
              {deal.chequeList.map((c) => (
                <tr key={c.id}>
                  <td>{c.direction === 'in' ? 'دریافتی' : 'پرداختی'}</td>
                  <td>{formatRial(c.amount)}</td>
                  <td>{c.due_date ? formatJalali(c.due_date) : '—'}</td>
                  <td>{CHQ_STATUS_FA[c.status] ?? c.status}</td>
                  <td>{c.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>
    </>
  );
}

/**
 * Reverse-entry a posted delivery (E16-18). A deliberate, reason-gated modal — this is an irreversible GL
 * action (E13-01): the original delivery stays on the books, an offsetting reversal is posted, and the order
 * line becomes deliverable again for a corrected dispatch. To FIX a delivery: reverse it, then re-deliver.
 */
function ReverseDeliveryModal({
  delivery,
  onClose,
  onDone,
}: {
  delivery: DeliveryRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (reason.trim().length < 3) return toast('دلیل برگشت لازم است (حداقل ۳ نویسه)', 'error');
    setBusy(true);
    const r = await api(`/api/deliveries/${delivery.id}/reverse`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('تحویل برگشت خورد — این قلم دوباره قابل تحویل است');
    onDone();
  }

  return (
    <Modal title="برگشت تحویل" onClose={onClose}>
      <div className="banner banner-warn" style={{ marginTop: 0 }}>
        این تحویل <b>برگشت</b> می‌خورد: {delivery.product_code} × {delivery.qty}. سند اصلی حفظ و یک
        سند برگشتِ خنثی‌کننده ثبت می‌شود؛ باقی‌ماندهٔ سفارش دوباره آزاد می‌شود. برای اصلاح، پس از
        برگشت، تحویل درست را ثبت کنید.
      </div>
      <Field label="دلیل برگشت" required>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثلاً: تعداد اشتباه ثبت شده بود"
        />
      </Field>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button variant="danger" onClick={submit} loading={busy}>
          برگشت تحویل
        </Button>
        <Button variant="secondary" onClick={onClose}>
          انصراف
        </Button>
      </div>
    </Modal>
  );
}
